/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import _ from 'lodash';
import errorFormatter from '@/utils/errorFormatter';
import {
  createProgram,
  loadProgramByKey,
  updateProgramByKey,
  deleteProgram,
  loadSeasons,
} from '@/program/api';
import {
  validateContentCategory,
  validateTitle,
  validateProgramKey,
  getErrorMessageFromErrorStatus,
  valdiateEmail,
} from '../../validator/contentValidator';

const state = {
  currentProgram: {},
  seasons: [],
  loadProgramState: {
    status: 'idle',
    error: null,
  },
  saveProgramState: {
    status: 'idle',
    error: null,
  },
};

const defaultState = {
  currentProgram: {},
  seasons: [],
  loadProgramState: {
    status: 'idle',
    error: null,
  },
  saveProgramState: {
    status: 'idle',
    error: null,
  },
};

const getters = {
  currentProgram: (state) => state.currentProgram,
};

const mutations = {
  initiateCurrentProgram(state) {
    state.currentProgram = {
      personListType: 'ผู้ดำเนินรายการ',
      persons: [],
      tags: [],
      features: {
        audioDesc: false,
        restrictedLicense: false,
        soundtrack: false,
        closedCaptions: false,
        signLanguage: false,
      },
      broadcastingChannels: {
        facebook: false,
        tv: false,
        twitter: false,
        web: false,
        youtube: false,
      },
      attachment: {
        name: 'ไฟล์แนบ',
        files: [],
      },
    };
  },
  setCurrentProgram(state, programData) {
    state.currentProgram = { ...state.currentProgram, ...programData };

    const contentCategoryError = validateContentCategory(
      state.currentProgram.contentCategory,
      state.currentProgram.status,
      state.saveProgramState.error,
    );
    const titleError = validateTitle(state.currentProgram.title);
    const keyError = validateProgramKey(state.currentProgram.key);
    const emailError = valdiateEmail(state.currentProgram.mail);

    state.saveProgramState.error = {
      ...state.saveProgramState.error,
      ...contentCategoryError,
      ...titleError,
      ...keyError,
      ...emailError,
      serverError: null,
    };
  },
  setSeason(state, seasonData) {
    state.seasons = seasonData;
  },
  setLoadProgramState(state, message) {
    switch (message.type) {
      case 'loading':
        state.loadProgramState.status = 'loading';
        state.loadProgramState.error = null;
        break;
      case 'success':
        state.loadProgramState.status = 'success';
        break;
      case 'error':
        state.loadProgramState.status = 'error';
        state.loadProgramState.error = message.payload;
        break;
      default:
        state.loadProgramState.status = 'idle';
        state.loadProgramState.error = null;
    }
  },
  setSaveProgramState(state, message) {
    switch (message.type) {
      case 'saving':
        state.saveProgramState.status = 'saving';
        state.saveProgramState.error = null;
        break;
      case 'success':
        state.saveProgramState.status = 'saved';
        break;
      case 'error':
        state.saveProgramState.status = 'error';
        state.saveProgramState.error = message.payload;
        break;
      default:
        state.saveProgramState.status = 'idle';
        state.saveProgramState.error = null;
    }
  },
  setProgramAttachmentID(state, attachmentID) {
    state.currentProgram.attachmentId = attachmentID;
  },
  setProgramGalleryID(state, galleryID) {
    state.currentProgram.galleryId = galleryID;
  },
  destroyCurrentProgram(state) {
    Object.assign(state, _.cloneDeep(defaultState));
  },
};

const actions = {
  async initiateCurrentProgram({ commit }) {
    commit('initiateCurrentProgram');
  },
  async loadProgramByKey({ dispatch, commit }, programId) {
    try {
      commit('destroyCurrentProgram');
      dispatch('destroyCurrentGalleryState');
      dispatch('resetAttachment');
      commit('setLoadProgramState', { type: 'loading' });
      const response = await loadProgramByKey(programId);
      await dispatch('setCurrentProgram', response.data);
      if (response.data.programType === 'program') {
        const responseSeason = await loadSeasons(programId);
        await dispatch('setSeason', responseSeason.data.data);
      } else if (response.data.programType === 'season') {
        const responseSeason = await loadSeasons(response.data.parentProgramId);
        await dispatch('setSeason', responseSeason.data.data);
      }
      if (response.data.attachmentId) {
        await dispatch('loadAttachment', response.data.attachmentId);
      }
      if (response.data.galleryId) {
        await dispatch('loadCurrentGallery', response.data.galleryId);
      }
      commit('setLoadProgramState', { type: 'success' });
    } catch (error) {
      commit('setLoadProgramState', {
        type: 'error',
        payload: error.response,
      });
    }
  },
  async createProgram({ dispatch, commit }, body) {
    try {
      commit('setSaveProgramState', { type: 'saving' });
      const response = await createProgram(body.payload);
      await dispatch('setCurrentProgram', response.data);
      commit('setSaveProgramState', { type: 'success' });
    } catch (error) {
      commit('setSaveProgramState', {
        type: 'error',
        payload: {
          ...errorFormatter.methods.transformErrorData(error.response.data),
          ...getErrorMessageFromErrorStatus(error.response.status),
        },
      });
    }
  },
  async updateProgramByKey({ dispatch, commit }, body) {
    try {
      commit('setSaveProgramState', { type: 'saving' });
      const response = await updateProgramByKey(body.programId, body.payload);
      await dispatch('setCurrentProgram', response.data);
      commit('setSaveProgramState', { type: 'success' });
    } catch (error) {
      commit('setSaveProgramState', {
        type: 'error',
        payload: {
          ...errorFormatter.methods.transformErrorData(error.response.data),
          ...getErrorMessageFromErrorStatus(error.response.status),
        },
      });
    }
  },
  async deleteProgram({ commit }, programId) {
    try {
      await deleteProgram(programId);
      commit('destroyCurrentProgram');
    } catch (error) {
      commit('setSaveProgramState', {
        type: 'error',
        payload: {
          ...errorFormatter.methods.transformErrorData(error.response.data),
          ...getErrorMessageFromErrorStatus(error.response.status),
        },
      });
    }
  },
  setCurrentProgram({ commit }, programData) {
    commit('setCurrentProgram', programData);
  },
  destroyCurrentProgram({ commit }) {
    commit('destroyCurrentProgram');
  },
  setProgramAttachmentID({ commit }, attachmentID) {
    commit('setProgramAttachmentID', attachmentID);
  },
  setSeason({ commit }, seasonData) {
    commit('setSeason', seasonData);
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
